import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Calendar, MapPin, Users, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import { useTranslation } from 'react-i18next'
import EventDetail from './EventDetail'

interface Event {
  id: string
  title: string
  description: string
  date: string
  location: string
  image: string
  imageAlt: string
  type: 'event' | 'seminar' | 'sponsorship'
  url?: string
  fullDescription?: string
  attendees?: string
  organizer?: string
}

const EventsSeminars: React.FC = () => {
  const { t: tContext } = useLanguage()
  const { t } = useTranslation()
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null)
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  
  // Carousel images for event cards
  const carouselImages = [
    '/events/tec-2025/img-1.jpeg',
    '/events/tec-2025/img-2.jpeg',
    '/events/tec-2025/img-3.jpeg',
    '/events/tec-2025/img-4.jpeg',
    '/events/tec-2025/img-5.jpeg'
  ]
  
  // Get events from translations (will be populated in i18n files)
  const eventsRaw = t('events.eventsList', { returnObjects: true })
  const events: Event[] = Array.isArray(eventsRaw) ? eventsRaw as Event[] : []

  // Track carousel state per event
  const [eventCarouselIndices, setEventCarouselIndices] = useState<Record<string, number>>({})

  // Initialize carousel indices when events change
  useEffect(() => {
    setEventCarouselIndices(prev => {
      const newIndices = { ...prev }
      let hasChanges = false
      events.forEach((event) => {
        if (!(event.id in newIndices)) {
          newIndices[event.id] = 0
          hasChanges = true
        }
      })
      return hasChanges ? newIndices : prev
    })
  }, [events.length])

  // Get carousel index for an event
  const getEventImageIndex = (eventId: string) => {
    return eventCarouselIndices[eventId] ?? 0
  }

  const setEventImageIndex = (eventId: string, index: number) => {
    setEventCarouselIndices(prev => ({ ...prev, [eventId]: index }))
  }

  // Auto-advance carousel for each event - pause when modal is open
  useEffect(() => {
    // Don't start intervals if modal is open
    if (isDetailOpen) {
      return
    }

    const intervals: ReturnType<typeof setInterval>[] = []
    
    events.forEach((event) => {
      const interval = setInterval(() => {
        setEventCarouselIndices(prev => ({
          ...prev,
          [event.id]: ((prev[event.id] || 0) + 1) % carouselImages.length
        }))
      }, 5000) // Change image every 5 seconds
      intervals.push(interval)
    })

    return () => {
      intervals.forEach(interval => clearInterval(interval))
    }
  }, [events.length, carouselImages.length, isDetailOpen])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'event':
        return 'bg-blue-500'
      case 'seminar':
        return 'bg-purple-500'
      case 'sponsorship':
        return 'bg-green-500'
      default:
        return 'bg-gray-500'
    }
  }

  const getTypeLabel = (type: string) => {
    return tContext(`events.types.${type}`)
  }

  return (
    <section id="events-seminars" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50" role="region" aria-label="Events and Seminars section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={containerVariants}
          className="text-center mb-16"
        >
          <motion.h1
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            {tContext('events.title')}
          </motion.h1>
          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed"
          >
            {tContext('events.description')}
          </motion.p>
        </motion.div>

        {/* Events Grid */}
        {events && events.length > 0 ? (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {events.map((event) => {
              const currentIndex = getEventImageIndex(event.id)
              const goToPrevious = () => {
                setEventImageIndex(event.id, (currentIndex - 1 + carouselImages.length) % carouselImages.length)
              }
              const goToNext = () => {
                setEventImageIndex(event.id, (currentIndex + 1) % carouselImages.length)
              }

              return (
                <motion.div
                  key={event.id}
                  variants={itemVariants}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 group"
                >
                  {/* Image Carousel */}
                  <div className="relative h-48 w-full overflow-hidden">
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.img
                        key={currentIndex}
                        src={carouselImages[currentIndex]}
                        alt={event.imageAlt || event.title}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.6, ease: "easeInOut" }}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 ease-out select-none"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                        onDragStart={(e) => e.preventDefault()}
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement
                          target.src = '/events/tec-2025/img-1.jpeg'
                        }}
                      />
                    </AnimatePresence>

                    {/* Type Badge */}
                    <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-white text-xs font-semibold z-10 ${getTypeColor(event.type)}`}>
                      {getTypeLabel(event.type)}
                    </div>

                    {/* Navigation Arrows */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        goToPrevious()
                      }}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 rounded-full p-1.5 shadow-lg transition-all z-10 opacity-0 group-hover:opacity-100"
                      aria-label="Previous image"
                    >
                      <ChevronLeft className="w-4 h-4 text-white" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        goToNext()
                      }}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 rounded-full p-1.5 shadow-lg transition-all z-10 opacity-0 group-hover:opacity-100"
                      aria-label="Next image"
                    >
                      <ChevronRight className="w-4 h-4 text-white" />
                    </button>
                  </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                    {event.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {event.description}
                  </p>

                  {/* Event Details */}
                  <div className="space-y-2 mb-4">
                    {event.date && (
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span>{event.date}</span>
                      </div>
                    )}
                    {event.location && (
                      <div className="flex items-center text-sm text-gray-500">
                        <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span>{event.location}</span>
                      </div>
                    )}
                  </div>

                  {/* Action Button */}
                  <button
                    onClick={() => {
                      setSelectedEvent(event)
                      setIsDetailOpen(true)
                    }}
                    className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                  >
                    {tContext('events.learnMore')}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </button>
                </div>
              </motion.div>
            )
            })}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-xl text-gray-600">{tContext('events.noEvents')}</p>
          </motion.div>
        )}

        {/* Event Detail Modal */}
        <EventDetail
          event={selectedEvent}
          isOpen={isDetailOpen}
          onClose={() => {
            setIsDetailOpen(false)
            setSelectedEvent(null)
          }}
        />
      </div>
    </section>
  )
}

export default EventsSeminars

